<script setup lang="ts">
import { useState } from '#app'
import { getByPath, setByPath, inject } from '#imports'
import { onMounted, ref, toRaw } from 'vue'
import { useChooseFile } from '~/layers/cms/app/composables/chooseFile'
import { useChoosePage } from '~/layers/cms/app/composables/choosePage'
import { getcurrentChosenElement, getcurrentChosenElementDefaults, update_cms_values } from '~/layers/cms/app/plugins/cms-custom-component-list'
import { useCmsEditStore } from '~/layers/cms/app/stores/cmsEditStore'
import type { CmsCustomComponentType, CmsData, CustomComponentValue } from '~/layers/cms/app/types/cmsTypes'

const component_values = ref<CmsCustomComponentType[]>()

const { chooseFile } = useChooseFile()

const swapFilePath = async (variable: CustomComponentValue) => {
  const newSrc = await chooseFile()
  if (newSrc) variable.value = newSrc
}

const cmsData = useState<CmsData>('cmsData')

const path = ref<string[]>([])
let elem_defaults: CmsCustomComponentType = {}

onMounted(() => {
  const element = getcurrentChosenElement()
  elem_defaults = getcurrentChosenElementDefaults() as CmsCustomComponentType
  if (!element || !elem_defaults) return

  const data = getByPath(cmsData.value, element)
  path.value = [...element, 'component_data']
  component_values.value = Array.isArray(data?.component_data)
    ? (data.component_data as CmsCustomComponentType[]).map(v =>
        JSON.parse(JSON.stringify({ ...elem_defaults, ...v }))
      )
    : [JSON.parse(JSON.stringify(elem_defaults))]
})

const dialogRef = inject<DialogRef>('dialogRef')
interface DialogRef {
  value: {
    data: {
      defaultGroup: string
    }
    close(): void
  }
}

const closeDialog = () => {
  if (!dialogRef) return
  dialogRef.value.close()
}

const save = () => {
  if (!component_values.value) return

  const editStore = useCmsEditStore()
  const element = getcurrentChosenElement()
  const data = getByPath(cmsData.value, element as string[])

  setByPath(cmsData.value, path.value, component_values.value)
  const newValues = component_values.value

  update_cms_values(newValues)

  const data2 = getByPath(cmsData.value, element as string[])

  closeDialog()
}

const { choosePage } = useChoosePage()

const chooseLinkFromSites = async (value: CustomComponentValue) => {
  const newSrc = await choosePage()
  if (newSrc) {
    value.value = newSrc
  }
}

const addElement = () => {
  if (!elem_defaults) return
  if (!component_values.value) component_values.value = []
  component_values.value.unshift(structuredClone(toRaw(elem_defaults)))
}

const removeElement = (index: number) => {
  if (!component_values.value) return
  component_values.value.splice(index, 1)
}

const changeOrderPrev = (index: number) => {
  if (!component_values.value) return
  const arr = component_values.value
  if (index <= 0 || !arr[index] || !arr[index - 1]) return
  const tmp = arr[index - 1] as CmsCustomComponentType
  arr[index - 1] = arr[index] as CmsCustomComponentType
  arr[index] = tmp
}

const changeOrderNext = (index: number) => {
  if (!component_values.value) return
  const arr = component_values.value
  if (index >= arr.length - 1 || !arr[index] || !arr[index + 1]) return
  const tmp = arr[index + 1] as CmsCustomComponentType
  arr[index + 1] = arr[index] as CmsCustomComponentType
  arr[index] = tmp
}
</script>

<template>
  <div class="flex flex-col gap-6 p-4">
    <div class="flex items-center justify-between gap-4 pb-4 border-b border-surface-200 dark:border-surface-700">
      <span class="text-lg font-bold">Elementy: {{ component_values?.length || 0 }}</span>
      <div class="flex gap-2">
        <Button
          label="Dodaj element"
          icon="pi pi-plus"
          size="small"
          @click="addElement"
        />
        <Button
          label="Zapisz"
          icon="pi pi-save"
          size="small"
          severity="success"
          @click="save"
        />
      </div>
    </div>

    <div
      v-for="(comp, comp_index) in component_values"
      :key="JSON.stringify(comp)"
      class="flex flex-col gap-4 border border-surface-300 dark:border-surface-700 p-5 rounded-xl bg-surface-50 dark:bg-surface-900/50 shadow-sm"
    >
      <div class="flex justify-between items-center bg-surface-200/50 dark:bg-surface-800 p-2 rounded-lg">
        <span class="font-bold px-2">#{{ comp_index + 1 }}</span>
        <div class="flex gap-2">
          <Button
            v-if="comp_index !== 0"
            icon="pi pi-arrow-up"
            severity="secondary"
            variant="text"
            rounded
            @click="changeOrderPrev(comp_index)"
          />
          <Button
            v-if="component_values && comp_index !== component_values.length - 1"
            icon="pi pi-arrow-down"
            severity="secondary"
            variant="text"
            rounded
            @click="changeOrderNext(comp_index)"
          />
          <Button
            icon="pi pi-trash"
            severity="danger"
            variant="text"
            rounded
            @click="removeElement(comp_index)"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          v-for="cos in comp"
          :key="JSON.stringify(cos)"
          class="flex flex-col gap-2"
        >
          <label class="text-sm font-semibold text-surface-600 dark:text-surface-400">
            {{ cos.setting_label }}
          </label>

          <div class="flex items-center w-full gap-3">
            <div
              v-if="cos.settingType === 'link'"
              class="flex w-full gap-2"
            >
              <InputText
                v-model="(cos.value as string)"
                class="flex-auto"
                size="small"
              />
              <Button
                icon="pi pi-link"
                severity="secondary"
                size="small"
                @click="chooseLinkFromSites(cos)"
              />
            </div>

            <Select
              v-else-if="cos.settingType === 'select'"
              v-model="(cos.value as string)"
              :options="cos.selectValues"
              class="w-full"
            />

            <InputText
              v-else-if="cos.settingType === 'text'"
              v-model="(cos.value as string)"
              class="w-full"
            />

            <div
              v-else-if="cos.settingType === 'color'"
              class="flex items-center gap-2"
            >
              <input
                v-model="cos.value"
                type="color"
                class="w-10 h-10 rounded border border-surface-300 dark:border-surface-700 bg-transparent cursor-pointer"
              >
              <span class="font-mono text-xs uppercase">{{ cos.value }}</span>
            </div>

            <ToggleSwitch
              v-else-if="cos.settingType === 'boolean'"
              v-model="cos.value"
            />

            <div
              v-else-if="cos.settingType === 'image'"
              class="flex items-center gap-3"
            >
              <NuxtImg
                :src="(cos.value as string)"
                class="w-24 h-24 object-cover rounded-lg border border-surface-300 dark:border-surface-700 cursor-pointer hover:brightness-75 transition-all"
                alt="image"
                width="96"
                height="96"
                loading="lazy"
                format="webp"
                quality="100"
                @click="swapFilePath(cos)"
              />
              <Button
                severity="danger"
                variant="text"
                size="small"
                icon="pi pi-times"
                label="Usuń"
                @click="cos.value = ''"
              />
            </div>
            <div
              v-else-if="cos.settingType === 'video'"
              class="flex items-center gap-3"
            >
              <video
                :src="cos.value"
                preload="metadata"
                muted
                playsinline
                :poster="`${cos.value}#t=1`"
                class="w-24 h-24 object-cover rounded-lg border border-surface-300 dark:border-surface-700 cursor-pointer hover:brightness-75 transition-all"
                @click="swapFilePath(cos)"
              />
              <InputText
                v-model="(cos.value as string)"
                placeholder="Wklej link"
                class="flex-1"
              />
              <Button
                severity="danger"
                variant="text"
                size="small"
                icon="pi pi-times"
                label="Usuń"
                @click="cos.value = ''"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
